import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal, flushSync } from "react-dom";
import Typography from "../Typography";
import ErrorMsg from "../ErrorMsg";
import Checkbox from "../Checkbox/Checkbox";
import Avatar from "../Avatar/Avatar";
import { RiArrowUpSLine, RiCloseLine } from "react-icons/ri";

function getOptionSortLabel(opt) {
  if (typeof opt === "string") return opt;
  return opt?.label ?? opt?.value ?? "";
}

/** Searchable filter text — includes sublabel when present. */
function getOptionSearchText(opt) {
  if (typeof opt === "string") return opt;
  if (opt?.searchText) return String(opt.searchText);
  const label = opt?.label ?? opt?.value ?? "";
  const sub = opt?.sublabel ?? opt?.subLabel ?? "";
  return [sub, label].filter(Boolean).join(" ");
}

/** "All" and "All …" (e.g. All Clients) stay above A–Z sort in filter dropdowns */
function isAllSentinelOption(opt) {
  const s = String(getOptionSortLabel(opt)).trim();
  if (!s) return false;
  return /^all(\s|$)/i.test(s);
}

function compareDropdownOptions(a, b) {
  const aValue = typeof a === "string" ? a : a.label || a.value || "";
  const bValue = typeof b === "string" ? b : b.label || b.value || "";

  const aNum = parseFloat(aValue);
  const bNum = parseFloat(bValue);

  if (
    !isNaN(aNum) &&
    !isNaN(bNum) &&
    aValue.toString() === aNum.toString() &&
    bValue.toString() === bNum.toString()
  ) {
    return aNum - bNum;
  }

  return aValue.toString().toLowerCase().localeCompare(bValue.toString().toLowerCase());
}

const Dropdown = React.forwardRef(
  (
    {
      label = "",
      name,
      value,
      onChange,
      options = [],
      placeholder = "Select...",
      error = "",
      disabled = false,
      className = "",
      noWrapper = false,
      required = false,
      searchable = false,
      multiselect = false,
      allowAddOption = false,
      onAddOption,
      addOptionLabel = "Add",
      /** Show a first row that clears the selection (single → "", multiselect → []). */
      clearable = true,
      clearOptionLabel = "Clear selection",
      /**
       * When true, keep `options` order as provided (no A–Z sort).
       * Use for chronologically ordered lists (e.g. month/year pickers).
       */
      preserveOptionOrder = false,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [localOptions, setLocalOptions] = useState(options);
    /** Fixed layer position for portaled menu (escapes overflow:hidden / table clipping) */
    const [menuLayout, setMenuLayout] = useState(null);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const menuPanelRef = useRef(null);
    const setRef = useCallback(
      (node) => {
        dropdownRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    // Keep localOptions in sync with incoming options (drop null/undefined so sort/render never throws)
    useEffect(() => {
      const list = Array.isArray(options) ? options : [];
      setLocalOptions(list.filter((o) => o !== undefined && o !== null));
    }, [options]);

    // Normalize value to array for multiselect
    const selectedValues = multiselect ? (Array.isArray(value) ? value : value ? [value] : []) : [];

    // Sort options in ascending order (by text or number); pin "All" / "All …" to the top — unless order is fixed
    const sortedOptions = (() => {
      if (preserveOptionOrder) {
        return [...localOptions];
      }
      const allGroup = [];
      const rest = [];
      for (const o of localOptions) {
        (isAllSentinelOption(o) ? allGroup : rest).push(o);
      }
      allGroup.sort(compareDropdownOptions);
      rest.sort(compareDropdownOptions);
      return [...allGroup, ...rest];
    })();

    // Filter options based on search term (for searchable mode)
    const filteredOptions =
      searchable || (allowAddOption && isOpen)
        ? sortedOptions.filter((option) => {
            const haystack = typeof option === "string" ? option : getOptionSearchText(option);
            return haystack.toLowerCase().includes(searchTerm.toLowerCase());
          })
        : sortedOptions;

    const canAddOption = (() => {
      if (!allowAddOption || disabled) return false;
      const text = String(searchTerm || "").trim();
      if (!text) return false;
      const lower = text.toLowerCase();
      const exists = (localOptions || []).some((opt) => {
        const label = typeof opt === "string" ? opt : (opt.label ?? opt.value);
        const val = typeof opt === "string" ? opt : opt.value;
        return (
          String(label || "")
            .trim()
            .toLowerCase() === lower ||
          String(val || "")
            .trim()
            .toLowerCase() === lower
        );
      });
      return !exists;
    })();

    const handleAddOption = () => {
      const text = String(searchTerm || "").trim();
      if (!text) return;
      const newOption = { value: text, label: text };
      setLocalOptions((prev) => [...(Array.isArray(prev) ? prev : []), newOption]);
      onAddOption?.(newOption);
      // Auto-select newly added option
      handleSelect(newOption);
      setSearchTerm("");
    };

    const updateMenuLayout = useCallback(() => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const pad = 8;
      const preferredMax = 240; // matches max-h-60
      const spaceBelow = window.innerHeight - rect.bottom - pad;
      const spaceAbove = rect.top - pad;
      const openUpward = spaceBelow < 120 && spaceAbove > spaceBelow;
      const maxHeight = Math.min(preferredMax, Math.max(80, openUpward ? spaceAbove : spaceBelow));
      if (openUpward) {
        setMenuLayout({
          left: rect.left,
          width: rect.width,
          maxHeight,
          bottom: window.innerHeight - rect.top + 4,
          openUpward: true,
        });
      } else {
        setMenuLayout({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          maxHeight,
          openUpward: false,
        });
      }
    }, []);

    useLayoutEffect(() => {
      if (!isOpen) {
        setMenuLayout(null);
        return;
      }
      updateMenuLayout();
    }, [isOpen, updateMenuLayout]);

    useEffect(() => {
      if (!isOpen) return;
      const onScrollOrResize = () => updateMenuLayout();
      window.addEventListener("scroll", onScrollOrResize, true);
      window.addEventListener("resize", onScrollOrResize);
      return () => {
        window.removeEventListener("scroll", onScrollOrResize, true);
        window.removeEventListener("resize", onScrollOrResize);
      };
    }, [isOpen, updateMenuLayout]);

    // Close dropdown when clicking outside (menu is portaled — check menuPanelRef too)
    useEffect(() => {
      const handleClickOutside = (event) => {
        const t = event.target;
        if (dropdownRef.current?.contains(t)) return;
        if (menuPanelRef.current?.contains(t)) return;
        setIsOpen(false);
        setSearchTerm("");
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleInputClick = () => {
      if (disabled) return;
      if (isOpen) {
        setIsOpen(false);
        setSearchTerm("");
        return;
      }
      flushSync(() => {
        setIsOpen(true);
      });
      updateMenuLayout();
    };

    /** Single-select: find option matching current value (string-safe). */
    const findSelectedOptionSingle = () => {
      if (multiselect || value === "" || value == null) return null;
      return localOptions.find((option) => {
        const optionValue = typeof option === "string" ? option : option.value;
        return String(optionValue) === String(value);
      });
    };

    // Get display value for single select
    const getDisplayValue = () => {
      if (multiselect) return ""; // Multiselect uses tags, not input value
      if (!value && value !== 0) return "";
      const selectedOption = findSelectedOptionSingle();
      if (selectedOption) {
        return typeof selectedOption === "string"
          ? selectedOption
          : selectedOption.displayLabel || selectedOption.label || selectedOption.value;
      }
      return value;
    };

    const selectedOptionSingle = !multiselect ? findSelectedOptionSingle() : null;
    const SelectedIcon =
      selectedOptionSingle && typeof selectedOptionSingle === "object"
        ? selectedOptionSingle.icon
        : null;
    const selectedIconColor =
      selectedOptionSingle && typeof selectedOptionSingle === "object"
        ? selectedOptionSingle.color
        : "";

    // Get selected options for multiselect
    const getSelectedOptions = () => {
      return selectedValues.map((val) => {
        const option = localOptions.find((opt) => {
          const optValue = typeof opt === "string" ? opt : opt.value;
          return optValue === val;
        });
        if (option) {
          return {
            value: typeof option === "string" ? option : option.value,
            label: typeof option === "string" ? option : option.label || option.value,
          };
        }
        return { value: val, label: val };
      });
    };

    const handleSelect = (option) => {
      if (option === undefined || option === null) return;
      const optionValue = typeof option === "string" ? option : option?.value;

      if (multiselect) {
        const newSelectedValues = selectedValues.includes(optionValue)
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue];
        onChange({ target: { name, value: newSelectedValues } });
        // Don't close dropdown in multiselect mode
      } else {
        onChange({ target: { name, value: optionValue } });
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    const handleRemoveTag = (e, tagValue) => {
      e.stopPropagation();
      if (multiselect) {
        const newSelectedValues = selectedValues.filter((v) => v !== tagValue);
        onChange({ target: { name, value: newSelectedValues } });
      }
    };

    const handleClearAll = (e) => {
      e.stopPropagation();
      if (multiselect) {
        onChange({ target: { name, value: [] } });
      }
    };

    const isOptionSelected = (optionValue) => {
      if (multiselect) {
        return selectedValues.includes(optionValue);
      }
      return String(value) === String(optionValue);
    };

    const hasSelection = multiselect
      ? selectedValues.length > 0
      : value !== "" && value != null && !(typeof value === "number" && Number.isNaN(value));

    const handleClearSelection = (e) => {
      e.stopPropagation();
      if (!clearable || disabled) return;
      if (multiselect) {
        onChange({ target: { name, value: [] } });
      } else {
        onChange({ target: { name, value: "" } });
        setIsOpen(false);
      }
      setSearchTerm("");
    };

    const menuPanelStyle =
      menuLayout != null
        ? {
            position: "fixed",
            zIndex: 100000,
            left: menuLayout.left,
            width: menuLayout.width,
            maxHeight: menuLayout.maxHeight,
            ...(menuLayout.top != null ? { top: menuLayout.top } : {}),
            ...(menuLayout.bottom != null ? { bottom: menuLayout.bottom } : {}),
          }
        : undefined;

    const menuDropdown = (
      <div
        ref={menuPanelRef}
        data-dropdown-portal
        className="flex flex-col bg-surface-primary border border-border-primary rounded-sm shadow-lg overflow-hidden"
        style={menuPanelStyle}
      >
        {(searchable || allowAddOption) && (
          <div className="p-2 border-b border-border-primary shrink-0">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canAddOption) {
                  e.preventDefault();
                  handleAddOption();
                }
              }}
              placeholder={searchable ? "Search..." : "Type to add..."}
              className="w-full text-sm border border-border-primary rounded-sm px-2 py-1.5 text-text-primary bg-surface-primary"
              autoFocus
            />
          </div>
        )}

        {clearable && !disabled && hasSelection && (
          <button
            type="button"
            onClick={handleClearSelection}
            className="w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors border-b border-border-primary text-text-secondary hover:bg-[var(--bg-secondary)] hover:text-brand-primary shrink-0"
          >
            {clearOptionLabel}
          </button>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto" data-dropdown-options-scroll>
          {canAddOption && (
            <div
              onClick={handleAddOption}
              className="px-3 py-2 text-sm cursor-pointer transition-colors text-text-primary hover:bg-[var(--bg-secondary)]"
            >
              {addOptionLabel} "{String(searchTerm || "").trim()}"
            </div>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const optionValue = typeof option === "string" ? option : option.value;
              const optionLabel =
                typeof option === "string" ? option : option.label || option.value;
              const isSelected = isOptionSelected(optionValue);

              const showAvatar = typeof option === "object" ? option.showAvatar : false;
              const avatarImageUrl =
                typeof option === "object" ? option.avatarUrl || option.avatarSrc : null;

              const Icon = typeof option === "object" ? option.icon : null;
              const iconColor = typeof option === "object" ? option.color : "";
              const optionSublabel =
                typeof option === "object" ? (option.sublabel ?? option.subLabel) : null;

              return (
                <div
                  key={index}
                  onClick={() => handleSelect(option)}
                  className={`px-3 py-2 text-sm cursor-pointer transition-colors flex items-center gap-2 ${
                    isSelected && multiselect
                      ? "bg-[var(--bg-secondary)] text-[var(--text-primary)]"
                      : isSelected
                        ? "text-white"
                        : "text-text-primary hover:bg-[var(--bg-secondary)]"
                  }`}
                  style={{
                    backgroundColor:
                      isSelected && multiselect
                        ? "var(--bg-secondary)"
                        : isSelected && !multiselect
                          ? "var(--theme-primary)"
                          : "transparent",
                  }}
                >
                  {multiselect && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelect(option)}
                        noWrapper={true}
                      />
                    </div>
                  )}

                  {Icon && !showAvatar && (
                    <Icon className={`text-lg ${iconColor || "text-text-secondary"}`} />
                  )}

                  {showAvatar && (
                    <Avatar
                      src={avatarImageUrl || undefined}
                      name={
                        typeof option === "object" && option.avatar
                          ? option.avatar
                          : optionLabel || ""
                      }
                      className="!w-7 !h-7 !min-w-[28px] !min-h-[28px] !text-xs shrink-0"
                    />
                  )}
                  <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                    {optionSublabel ? (
                      <span
                        className={`text-[11px] font-semibold uppercase tracking-wide truncate ${
                          isSelected && !multiselect ? "text-white/85" : "text-text-secondary"
                        }`}
                      >
                        {optionSublabel}
                      </span>
                    ) : null}
                    <span className="truncate">{optionLabel}</span>
                  </span>
                </div>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-text-secondary">No options found</div>
          )}
        </div>
      </div>
    );

    // Custom dropdown element (both searchable and non-searchable)
    const selectElement = (
      <div className={`relative ${className}`} ref={setRef}>
        <div className="relative" ref={triggerRef}>
          {multiselect ? (
            // Multiselect: Show tags in input field
            <div
              onClick={handleInputClick}
              className={`text-sm border rounded-sm px-2 py-1.5 bg-surface-primary cursor-pointer flex flex-wrap items-center gap-1.5 ${
                error ? "border-red-500" : "border-border-primary"
              } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
            >
              {getSelectedOptions().length > 0 ? (
                <>
                  {getSelectedOptions().map((option, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-primary)]"
                    >
                      <RiCloseLine
                        className="cursor-pointer hover:text-brand-primary transition-colors"
                        size={14}
                        onClick={(e) => handleRemoveTag(e, option.value)}
                      />
                      {option.label}
                    </span>
                  ))}
                  <div className="ml-auto flex items-center gap-2">
                    {selectedValues.length > 0 && (
                      <RiCloseLine
                        className="cursor-pointer text-[var(--text-secondary)] hover:text-brand-primary transition-colors"
                        size={18}
                        onClick={handleClearAll}
                      />
                    )}
                    <RiArrowUpSLine
                      className={`text-sm text-text-secondary transition-transform ${
                        !isOpen ? "rotate-180" : ""
                      }`}
                      size={16}
                    />
                  </div>
                </>
              ) : (
                <>
                  <span className="text-gray-600 text-sm flex-1">{placeholder}</span>
                  <div className="ml-auto">
                    <RiArrowUpSLine
                      className={`text-sm text-text-secondary transition-transform ${
                        !isOpen ? "rotate-180" : ""
                      }`}
                      size={16}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            // Single select: closed state is label only; avatars render in the open list when option.showAvatar
            <>
              {SelectedIcon ? (
                <SelectedIcon
                  className={`absolute left-3 top-1/2 z-10 -translate-y-1/2 text-lg pointer-events-none ${
                    selectedIconColor || "text-text-secondary"
                  }`}
                  aria-hidden
                />
              ) : null}
              <input
                type="text"
                readOnly
                value={getDisplayValue()}
                onClick={handleInputClick}
                disabled={disabled}
                className={`w-full text-sm border rounded-sm py-1.5 bg-surface-primary cursor-pointer pr-8 ${
                  SelectedIcon ? "pl-10" : "px-3"
                } ${error ? "border-red-500" : "border-border-primary"} ${
                  value || value === 0 ? "text-text-primary" : "text-gray-600"
                } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
                placeholder={placeholder}
              />
              {!value && value !== 0 && (
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-600 text-sm"></div>
              )}
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <RiArrowUpSLine
                  className={`text-sm text-text-secondary transition-transform ${
                    !isOpen ? "rotate-180" : ""
                  }`}
                  size={16}
                />
              </div>
            </>
          )}
        </div>

        {isOpen &&
          menuLayout &&
          typeof document !== "undefined" &&
          createPortal(menuDropdown, document.body)}
      </div>
    );

    if (noWrapper) {
      return selectElement;
    }

    return (
      <div className="w-full">
        {label && (
          <Typography
            variant="text2"
            component="label"
            htmlFor={name}
            className="font-medium text-text-primary block"
          >
            {label}
            {required && <span className="text-red-600 ml-1">*</span>}
          </Typography>
        )}
        {selectElement}
        {error && <ErrorMsg message={error} className="mt-1" />}
      </div>
    );
  }
);

Dropdown.displayName = "Dropdown";

export default Dropdown;
